import React from 'react';
import {Box, Checkbox, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {FULLSCREEN_OVERLAY_SX, FullscreenToggle} from '../../shared/Fullscreen';
import {PrimaryFilter} from '../../../../../../../../stores/irp-workbook.store';
import {TrendWindowBoundary} from '../../../utils/trendWindowBoundary';
import {CalendarDrillReport, CalendarHover} from './types';

interface CalendarViewProps {
    dailyDemand: Map<number, {demand: number; success: number; failure: number}>;
    globalMaxDemand: number;
    calendarVisibleYears: number[];
    calendarTotalPages: number;
    calendarVisibleBarrierIdSet: Set<number>;
    calendarPage: number;
    setCalendarPage: React.Dispatch<React.SetStateAction<number>>;
    calendarYearsPerPage: number;
    setCalendarYearsPerPage: React.Dispatch<React.SetStateAction<number>>;
    calendarLayout: 'standard' | 'yoy';
    setCalendarLayout: React.Dispatch<React.SetStateAction<'standard' | 'yoy'>>;
    calendarTrendOverlay: boolean;
    setCalendarTrendOverlay: React.Dispatch<React.SetStateAction<boolean>>;
    barrierTrendBoundary: TrendWindowBoundary | null;
    calendarHover: CalendarHover | null;
    setCalendarHover: React.Dispatch<React.SetStateAction<CalendarHover | null>>;
    primaryFilter: PrimaryFilter;
    colorSuccess: string;
    colorFailure: string;
    navigateToReportAnalysis: (reportId: number) => void;
    detailedFullscreen: boolean;
    setDetailedFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
    calendarDrillDate: Date | null;
    setCalendarDrillDate: React.Dispatch<React.SetStateAction<Date | null>>;
    calendarDrillReports: CalendarDrillReport[];
    calendarDrillPage: number;
    setCalendarDrillPage: React.Dispatch<React.SetStateAction<number>>;
    calendarDrillPageSize: number;
    setCalendarDrillPageSize: React.Dispatch<React.SetStateAction<number>>;
}

/** Calendar (day-grid) view of barrier demand over time, plus its day-drill dialog.
 *  Pure presentation — all data/state owned by the parent (Barrier Effectiveness).
 *  Rendered as a fragment; the Dialog is a portal so its tree position is irrelevant. */
export const CalendarView: React.FC<CalendarViewProps> = ({
    dailyDemand,
    globalMaxDemand,
    calendarVisibleYears,
    calendarTotalPages,
    calendarVisibleBarrierIdSet,
    calendarPage,
    setCalendarPage,
    calendarYearsPerPage,
    setCalendarYearsPerPage,
    calendarLayout,
    setCalendarLayout,
    calendarTrendOverlay,
    setCalendarTrendOverlay,
    barrierTrendBoundary,
    calendarHover,
    setCalendarHover,
    primaryFilter,
    colorSuccess,
    colorFailure,
    navigateToReportAnalysis,
    detailedFullscreen,
    setDetailedFullscreen,
    calendarDrillDate,
    setCalendarDrillDate,
    calendarDrillReports,
    calendarDrillPage,
    setCalendarDrillPage,
    calendarDrillPageSize,
    setCalendarDrillPageSize,
}) => (
    <>
        {((): React.ReactElement => {
            // Standard layout constants. GAP = 0 so adjacent cells touch edge-to-edge;
            // otherwise the month-outline corners develop a 1px discontinuity.
            const CELL = 13;
            const GAP = 0;
            const STRIDE = CELL + GAP;
            const LEFT_PAD = 56;    // year label + S M T W T F S labels
            const TOP_PAD = 28;     // month labels above the first band
            const BAND_H = 7 * STRIDE;
            const BAND_GAP = 20;    // gap between year bands
            const MAX_WEEKS = 54;   // safe upper bound (most years are 53)
            const RIGHT_PAD = 16;
            // Year-over-year (mini-month grid) constants.
            const YOY_CELL = 11;
            const YOY_STRIDE = YOY_CELL;
            const YOY_MONTH_W = 6 * YOY_STRIDE;   // each month max 6 weeks wide
            const YOY_MONTH_H = 7 * YOY_STRIDE;
            const YOY_COL_GAP = 10;               // between months horizontally
            const YOY_ROW_GAP = 18;               // between year rows
            const yearCount = calendarVisibleYears.length;
            const isYoY = calendarLayout === 'yoy';
            const SVG_W = isYoY
                ? LEFT_PAD + 12 * YOY_MONTH_W + 11 * YOY_COL_GAP + RIGHT_PAD
                : LEFT_PAD + MAX_WEEKS * STRIDE + RIGHT_PAD;
            const SVG_H = yearCount > 0
                ? (isYoY
                    ? TOP_PAD + yearCount * YOY_MONTH_H + (yearCount - 1) * YOY_ROW_GAP + 8
                    : TOP_PAD + yearCount * BAND_H + (yearCount - 1) * BAND_GAP + 8)
                : TOP_PAD;

            // Convert a date inside a year to (weekColumn, dayOfWeek). Anchors Jan 1
            // of each year at column 0 — each band is therefore self-contained.
            // Day-of-year is computed from calendar components (NOT from timestamps),
            // because timestamp arithmetic shifts by an hour at DST transitions, which
            // would mis-place every cell from mid-March onward by one column.
            const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
            const isLeap = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
            const cellPos = (date: Date): {col: number; row: number} => {
                const y = date.getFullYear();
                const m = date.getMonth();
                const dd = date.getDate();
                const jan1Dow = new Date(y, 0, 1).getDay();
                let dayOfYear = dd - 1;
                for (let i = 0; i < m; i++) {
                    dayOfYear += MONTH_DAYS[i] + (i === 1 && isLeap(y) ? 1 : 0);
                }
                return {col: Math.floor((dayOfYear + jan1Dow) / 7), row: date.getDay()};
            };

            // Mini-month cellPos for YoY layout — anchors each month at column 0,
            // so the first row contains the days of the first week of that month.
            const miniCellPos = (date: Date): {col: number; row: number} => {
                const dom = date.getDate() - 1;
                const day1Dow = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
                return {col: Math.floor((dom + day1Dow) / 7), row: date.getDay()};
            };

            // Log-scaled intensity. With skewed report distributions (one extreme day,
            // many quiet ones) a linear scale flattens mid-range days into near-white;
            // log lifts them so the calendar still reads at the middle of the range.
            // log(1+x) is used so demand=0 stays handled separately and demand=1 doesn't
            // jump straight to a saturated cell.
            const cellFill = (demand: number): string => {
                if (demand === 0) return '#f0f0f0';
                const ratio = globalMaxDemand > 0
                    ? Math.log1p(demand) / Math.log1p(globalMaxDemand)
                    : 0;
                const alpha = 0.2 + 0.8 * ratio;
                return `rgba(211, 47, 47, ${alpha})`;
            };

            const fromTs = primaryFilter.dateFrom?.getTime();
            const toTs = primaryFilter.dateTo?.getTime();

            // Trend-overlay stroke chooser — returns null when the cell should fall
            // back to the default gray border. Recent window = amber thick;
            // prior window = soft blue thin. Out-of-band cells return null.
            const trendStroke = (ts: number): {stroke: string; strokeWidth: number} | null => {
                if (!calendarTrendOverlay || !barrierTrendBoundary) return null;
                const {start, recentStart, end} = barrierTrendBoundary;
                if (ts >= recentStart && ts <= end) return {stroke: '#ff6f00', strokeWidth: 1.2};
                if (ts >= start && ts < recentStart) return {stroke: '#1976d2', strokeWidth: 0.9};
                return null;
            };

            const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const dayLetters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

            const fmtDate = (d: Date): string => d.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'});

            return (
                <Box
                    sx={{
                        flexGrow: 1,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        minHeight: 200,
                        ...(detailedFullscreen && {
                            ...FULLSCREEN_OVERLAY_SX,
                        }),
                    }}
                >
                    {/* Toolbar — caption, color legend, pagination */}
                    <Stack direction="row" alignItems="center" spacing={2} sx={{px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0}}>
                        <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                Aggregated across <strong>{calendarVisibleBarrierIdSet.size.toLocaleString()}</strong> barrier{calendarVisibleBarrierIdSet.size !== 1 ? 's' : ''} shown in the table — one cell per day, color intensity = report count.
                        </Typography>
                        <Box sx={{flex: 1}} />
                        {/* Layout toggle — pick standard one-year-per-row OR mini-month
                                grid (12 columns × N years) for year-over-year comparison. */}
                        <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>Layout</Typography>
                        <FormControl size="small" sx={{minWidth: 150}}>
                            <Select
                                value={calendarLayout}
                                onChange={(e): void => { setCalendarLayout(e.target.value as 'standard' | 'yoy'); setCalendarPage(0); }}
                                sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
                            >
                                <MenuItem value="standard" sx={{fontSize: '0.75rem'}}>Standard</MenuItem>
                                <MenuItem value="yoy" sx={{fontSize: '0.75rem'}}>Year-over-year</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', ml: 1}}>Years/page</Typography>
                        <FormControl size="small" sx={{minWidth: 60}}>
                            <Select
                                value={calendarYearsPerPage}
                                onChange={(e): void => { setCalendarYearsPerPage(Number(e.target.value)); setCalendarPage(0); }}
                                sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
                            >
                                {[3, 5, 10, 15, 20].map((n) => (
                                    <MenuItem key={n} value={n} sx={{fontSize: '0.75rem'}}>{n}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {/* Color legend — log-scaled. Midpoint of the gradient shows
                                √(max) so the analyst can read the scale's curvature. */}
                        <Tooltip title="Color intensity is log-scaled so mid-range days stay visible when one extreme day would otherwise wash everything out." arrow>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{cursor: 'help'}}>
                                <Typography variant="caption" sx={{color: 'text.secondary'}}>0</Typography>
                                <Box sx={{width: 100, height: 10, background: 'linear-gradient(to right, #f0f0f0, rgba(211,47,47,0.2), rgba(211,47,47,1))', border: '1px solid #d0d0d0', borderRadius: 0.5}} />
                                <Typography variant="caption" sx={{color: 'text.secondary'}}>{globalMaxDemand} <span style={{opacity: 0.6}}>(log)</span></Typography>
                            </Stack>
                        </Tooltip>
                        {/* Trend-overlay toggle — only meaningful when a trend window is
                                selected (allTime mode produces no boundary). Hidden when
                                there's no boundary to keep the toolbar uncluttered. */}
                        {barrierTrendBoundary && (
                            <Tooltip title="Outline recent-window cells in amber and prior-window cells in soft blue, so the calendar shows trend context alongside report density." arrow>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={calendarTrendOverlay}
                                            onChange={(e): void => setCalendarTrendOverlay(e.target.checked)}
                                            size="small"
                                            sx={{p: 0.5}}
                                        />
                                    }
                                    label={<Typography variant="caption" sx={{color: 'text.secondary'}}>Trend overlay</Typography>}
                                    sx={{m: 0, cursor: 'help'}}
                                />
                            </Tooltip>
                        )}
                        {/* Pagination */}
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconButton
                                size="small"
                                disabled={calendarPage >= calendarTotalPages - 1}
                                onClick={(): void => setCalendarPage((p) => Math.min(p + 1, calendarTotalPages - 1))}
                                title="Older years"
                            >
                                <Typography sx={{fontSize: '0.85rem'}}>◀</Typography>
                            </IconButton>
                            <Typography variant="caption" sx={{color: 'text.secondary', whiteSpace: 'nowrap'}}>
                                {calendarVisibleYears.length > 0
                                    ? (calendarVisibleYears[calendarVisibleYears.length - 1] === calendarVisibleYears[0]
                                        ? `${calendarVisibleYears[0]}`
                                        : `${calendarVisibleYears[calendarVisibleYears.length - 1]} – ${calendarVisibleYears[0]}`)
                                    : '—'}
                                {' '}({calendarPage + 1} / {calendarTotalPages})
                            </Typography>
                            <IconButton
                                size="small"
                                disabled={calendarPage <= 0}
                                onClick={(): void => setCalendarPage((p) => Math.max(p - 1, 0))}
                                title="Newer years"
                            >
                                <Typography sx={{fontSize: '0.85rem'}}>▶</Typography>
                            </IconButton>
                        </Stack>
                        {/* Exit-fullscreen button — only shown in fullscreen since the
                                regular toggle button sits outside this Box and gets covered. */}
                        {detailedFullscreen && (
                            <FullscreenToggle active onToggle={(): void => setDetailedFullscreen(false)} />
                        )}
                    </Stack>

                    {/* Calendar SVG canvas (scrolls horizontally if too wide) */}
                    <Box
                        sx={{flexGrow: 1, overflow: 'auto', position: 'relative', p: 1.5}}
                        onMouseLeave={(): void => setCalendarHover(null)}
                    >
                        <svg width={SVG_W} height={SVG_H} style={{display: 'block'}}>
                            <defs>
                                <pattern id="cal-hatch" patternUnits="userSpaceOnUse" width="4" height="4">
                                    <rect width="4" height="4" fill="#fafafa" />
                                    <path d="M0,4 L4,0" stroke="#d8d8d8" strokeWidth="0.6" />
                                </pattern>
                            </defs>

                            {/* Month labels — different positioning per layout. Standard
                                    uses each month's center-of-week column for the first visible
                                    year; YoY anchors labels to each mini-month's center. */}
                            {calendarVisibleYears.length > 0 && !isYoY && ((): React.ReactElement[] => {
                                const refYear = calendarVisibleYears[0];
                                return monthShort.map((m, i) => {
                                    const d15 = new Date(refYear, i, 15);
                                    const {col} = cellPos(d15);
                                    const x = LEFT_PAD + col * STRIDE + CELL / 2;
                                    return (
                                        <text key={m} x={x} y={TOP_PAD - 8} fontSize="10" fill="#888" textAnchor="middle">
                                            {m}
                                        </text>
                                    );
                                });
                            })()}
                            {calendarVisibleYears.length > 0 && isYoY && monthShort.map((m, i) => {
                                const x = LEFT_PAD + i * (YOY_MONTH_W + YOY_COL_GAP) + YOY_MONTH_W / 2;
                                return (
                                    <text key={m} x={x} y={TOP_PAD - 8} fontSize="10" fill="#888" textAnchor="middle">
                                        {m}
                                    </text>
                                );
                            })}

                            {/* Year-over-year mini-month grid */}
                            {isYoY && calendarVisibleYears.map((year, yearIdx) => {
                                const yearTopY = TOP_PAD + yearIdx * (YOY_MONTH_H + YOY_ROW_GAP);
                                const yearLabelX = 24;
                                const yearLabelY = yearTopY + YOY_MONTH_H / 2;
                                const cells: React.ReactElement[] = [];
                                for (let mi = 0; mi < 12; mi++) {
                                    const monthLeftX = LEFT_PAD + mi * (YOY_MONTH_W + YOY_COL_GAP);
                                    const daysInMonth = MONTH_DAYS[mi] + (mi === 1 && isLeap(year) ? 1 : 0);
                                    for (let dd = 1; dd <= daysInMonth; dd++) {
                                        const date = new Date(year, mi, dd);
                                        const ts = date.getTime();
                                        const inFilter = (fromTs == null || ts >= fromTs) && (toTs == null || ts <= toTs);
                                        const dayMidnight = new Date(date); dayMidnight.setHours(0, 0, 0, 0);
                                        const stats = dailyDemand.get(dayMidnight.getTime());
                                        const demand = stats?.demand ?? 0;
                                        const success = stats?.success ?? 0;
                                        const failure = stats?.failure ?? 0;
                                        const {col, row} = miniCellPos(date);
                                        const x = monthLeftX + col * YOY_STRIDE;
                                        const y = yearTopY + row * YOY_STRIDE;
                                        const fill = inFilter ? cellFill(demand) : 'url(#cal-hatch)';
                                        const trendBorder = inFilter ? trendStroke(ts) : null;
                                        const isoKey = `${year}-${String(mi + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
                                        cells.push(
                                            <rect
                                                key={isoKey}
                                                x={x}
                                                y={y}
                                                width={YOY_CELL}
                                                height={YOY_CELL}
                                                fill={fill}
                                                stroke={trendBorder?.stroke ?? '#e0e0e0'}
                                                strokeWidth={trendBorder?.strokeWidth ?? 0.5}
                                                style={{cursor: inFilter && demand > 0 ? 'pointer' : 'default'}}
                                                onMouseEnter={(e: React.MouseEvent<SVGRectElement>): void => {
                                                    if (!inFilter) { setCalendarHover(null); return; }
                                                    const svg = e.currentTarget.ownerSVGElement;
                                                    if (!svg) return;
                                                    const container = svg.parentElement;
                                                    if (!container) return;
                                                    const cellRect = e.currentTarget.getBoundingClientRect();
                                                    const contRect = container.getBoundingClientRect();
                                                    setCalendarHover({
                                                        date,
                                                        demand,
                                                        success,
                                                        failure,
                                                        x: cellRect.left + cellRect.width / 2 - contRect.left + container.scrollLeft,
                                                        y: cellRect.top - contRect.top + container.scrollTop,
                                                    });
                                                }}
                                                onClick={(): void => {
                                                    if (!inFilter || demand === 0) return;
                                                    setCalendarDrillDate(date);
                                                    setCalendarDrillPage(0);
                                                }}
                                            />
                                        );
                                    }
                                }
                                return (
                                    <g key={year}>
                                        <text
                                            x={yearLabelX}
                                            y={yearLabelY}
                                            fontSize="14"
                                            fill="#bbb"
                                            fontWeight="bold"
                                            transform={`rotate(-90, ${yearLabelX}, ${yearLabelY})`}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                        >
                                            {year}
                                        </text>
                                        {cells}
                                    </g>
                                );
                            })}

                            {/* Year bands (standard layout) */}
                            {!isYoY && calendarVisibleYears.map((year, bandIdx) => {
                                const bandTop = TOP_PAD + bandIdx * (BAND_H + BAND_GAP);
                                // Year label (rotated 90° on the far left)
                                const yearLabelX = 16;
                                const yearLabelY = bandTop + BAND_H / 2;
                                // Generate cells for every day of this year, plus month-boundary
                                // outlines on a second pass (drawn after cells so they sit on top).
                                const cells: React.ReactElement[] = [];
                                const outlines: React.ReactElement[] = [];
                                const dec31 = new Date(year, 11, 31);
                                for (let d = new Date(year, 0, 1); d.getTime() <= dec31.getTime(); d.setDate(d.getDate() + 1)) {
                                    const date = new Date(d);
                                    const ts = date.getTime();
                                    const inFilter = (fromTs == null || ts >= fromTs) && (toTs == null || ts <= toTs);
                                    const dayMidnight = new Date(date); dayMidnight.setHours(0, 0, 0, 0);
                                    const stats = dailyDemand.get(dayMidnight.getTime());
                                    const demand = stats?.demand ?? 0;
                                    const success = stats?.success ?? 0;
                                    const failure = stats?.failure ?? 0;
                                    const {col, row} = cellPos(date);
                                    const x = LEFT_PAD + col * STRIDE;
                                    const y = bandTop + row * STRIDE;
                                    const fill = inFilter ? cellFill(demand) : 'url(#cal-hatch)';
                                    const trendBorder = inFilter ? trendStroke(ts) : null;
                                    const isoKey = date.toISOString().slice(0, 10);
                                    cells.push(
                                        <rect
                                            key={isoKey}
                                            x={x}
                                            y={y}
                                            width={CELL}
                                            height={CELL}
                                            fill={fill}
                                            stroke={trendBorder?.stroke ?? '#e0e0e0'}
                                            strokeWidth={trendBorder?.strokeWidth ?? 0.5}
                                            style={{cursor: inFilter ? 'pointer' : 'default'}}
                                            onMouseEnter={(e: React.MouseEvent<SVGRectElement>): void => {
                                                if (!inFilter) { setCalendarHover(null); return; }
                                                const svg = (e.currentTarget.ownerSVGElement);
                                                if (!svg) return;
                                                const container = svg.parentElement;
                                                if (!container) return;
                                                const cellRect = e.currentTarget.getBoundingClientRect();
                                                const contRect = container.getBoundingClientRect();
                                                setCalendarHover({
                                                    date,
                                                    demand,
                                                    success,
                                                    failure,
                                                    x: cellRect.left + cellRect.width / 2 - contRect.left + container.scrollLeft,
                                                    y: cellRect.top - contRect.top + container.scrollTop,
                                                });
                                            }}
                                            // Drill-down — only meaningful for in-filter days with at
                                            // least one report; clicking opens the day's report list.
                                            onClick={(): void => {
                                                if (!inFilter || demand === 0) return;
                                                setCalendarDrillDate(date);
                                                setCalendarDrillPage(0);
                                            }}
                                        />
                                    );

                                    // Month-boundary outline. For each cell edge whose neighbor
                                    // is either out of the year band or in a different month,
                                    // draw a thin black line so each month reads as one shape.
                                    const dY = date.getFullYear();
                                    const dM = date.getMonth();
                                    const dD = date.getDate();
                                    const sameMonth = (other: Date): boolean => other.getFullYear() === dY && other.getMonth() === dM;
                                    // Top edge: above neighbor = previous day; only exists for row > 0.
                                    if (row === 0 || !sameMonth(new Date(dY, dM, dD - 1))) {
                                        outlines.push(<line key={`${isoKey}-t`} x1={x} y1={y} x2={x + CELL} y2={y} stroke="#333" strokeWidth={1} shapeRendering="crispEdges" />);
                                    }
                                    // Bottom edge: below neighbor = next day; only exists for row < 6.
                                    if (row === 6 || !sameMonth(new Date(dY, dM, dD + 1))) {
                                        outlines.push(<line key={`${isoKey}-b`} x1={x} y1={y + CELL} x2={x + CELL} y2={y + CELL} stroke="#333" strokeWidth={1} shapeRendering="crispEdges" />);
                                    }
                                    // Left edge: 7 days earlier (same row in previous week column).
                                    if (!sameMonth(new Date(dY, dM, dD - 7))) {
                                        outlines.push(<line key={`${isoKey}-l`} x1={x} y1={y} x2={x} y2={y + CELL} stroke="#333" strokeWidth={1} shapeRendering="crispEdges" />);
                                    }
                                    // Right edge: 7 days later (same row in next week column).
                                    if (!sameMonth(new Date(dY, dM, dD + 7))) {
                                        outlines.push(<line key={`${isoKey}-r`} x1={x + CELL} y1={y} x2={x + CELL} y2={y + CELL} stroke="#333" strokeWidth={1} shapeRendering="crispEdges" />);
                                    }
                                }
                                return (
                                    <g key={year}>
                                        <text
                                            x={yearLabelX}
                                            y={yearLabelY}
                                            fontSize="14"
                                            fill="#bbb"
                                            fontWeight="bold"
                                            transform={`rotate(-90, ${yearLabelX}, ${yearLabelY})`}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                        >
                                            {year}
                                        </text>
                                        {/* Weekday letters (S M T W T F S) on the left of each band */}
                                        {dayLetters.map((letter, i) => (
                                            <text
                                                key={i}
                                                x={LEFT_PAD - 8}
                                                y={bandTop + i * STRIDE + CELL / 2 + 3}
                                                fontSize="9"
                                                fill="#888"
                                                textAnchor="end"
                                            >
                                                {letter}
                                            </text>
                                        ))}
                                        {cells}
                                        {outlines}
                                    </g>
                                );
                            })}
                        </svg>

                        {/* Floating tooltip */}
                        {calendarHover && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: calendarHover.x,
                                    top: calendarHover.y - 8,
                                    transform: 'translate(-50%, -100%)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 0.5,
                                    fontSize: '0.7rem',
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none',
                                    zIndex: 10,
                                    lineHeight: 1.4,
                                }}
                            >
                                <div>{fmtDate(calendarHover.date)}</div>
                                <div>
                                    {calendarHover.demand} report{calendarHover.demand !== 1 ? 's' : ''}
                                </div>
                            </Box>
                        )}
                    </Box>
                </Box>
            );
        })()}
        <Dialog open={calendarDrillDate != null} onClose={(): void => setCalendarDrillDate(null)} maxWidth="md" fullWidth>
            <DialogTitle sx={{fontSize: '1rem'}}>
                {calendarDrillDate && (
                    <>
                            Reports on {calendarDrillDate.toLocaleDateString('en-US', {weekday: 'long', month: 'short', day: 'numeric', year: 'numeric'})}
                        {' '}({calendarDrillReports.length.toLocaleString()})
                    </>
                )}
            </DialogTitle>
            <DialogContent dividers>
                <table style={{borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem'}}>
                    <thead>
                        <tr>
                            <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Report</th>
                            <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Source</th>
                            <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Barrier interactions</th>
                            <th style={{textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {calendarDrillReports.slice(calendarDrillPage * calendarDrillPageSize, (calendarDrillPage + 1) * calendarDrillPageSize).map((r) => (
                            <tr key={r.reportId}>
                                <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>{r.uniqueId || `Report ${r.reportId}`}</td>
                                <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>{r.sourceName || '—'}</td>
                                <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>
                                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                        {r.barriers.map((b) => (
                                            <Box
                                                key={b.nodeId}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 1,
                                                    fontSize: '0.7rem',
                                                    backgroundColor: b.outcome === 'SUCCESS' ? `${colorSuccess}1A` : `${colorFailure}1A`,
                                                    color: b.outcome === 'SUCCESS' ? colorSuccess : colorFailure,
                                                    border: '1px solid',
                                                    borderColor: b.outcome === 'SUCCESS' ? `${colorSuccess}55` : `${colorFailure}55`,
                                                }}
                                                title={b.name}
                                            >
                                                {b.uniqueId || b.name || `Barrier ${b.nodeId}`}
                                                <Box component="span" sx={{fontWeight: 'bold'}}>{b.outcome === 'SUCCESS' ? '✓' : '✗'}</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </td>
                                <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center'}}>
                                    <Tooltip title="View report details" arrow>
                                        <IconButton
                                            size="small"
                                            sx={{padding: '2px'}}
                                            onClick={(): void => {
                                                setCalendarDrillDate(null);
                                                navigateToReportAnalysis(r.reportId);
                                            }}
                                        >
                                            <VisibilityIcon sx={{fontSize: 16}} />
                                        </IconButton>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {calendarDrillReports.length > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 1}}>
                        <Typography variant="caption" color="text.secondary">Reports per page:</Typography>
                        <TextField
                            select
                            size="small"
                            value={calendarDrillPageSize}
                            onChange={(e): void => { setCalendarDrillPageSize(Number(e.target.value)); setCalendarDrillPage(0); }}
                            sx={{minWidth: 64, '& .MuiInputBase-input': {fontSize: '0.75rem', py: 0.5}}}
                        >
                            {[10, 25, 50, 100].map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                            ))}
                        </TextField>
                        <Typography variant="caption" color="text.secondary" sx={{flex: 1, textAlign: 'right'}}>
                            {calendarDrillPage * calendarDrillPageSize + 1}–{Math.min((calendarDrillPage + 1) * calendarDrillPageSize, calendarDrillReports.length)} of {calendarDrillReports.length}
                        </Typography>
                        <IconButton
                            size="small"
                            disabled={calendarDrillPage === 0}
                            onClick={(): void => setCalendarDrillPage((p) => p - 1)}
                        >
                            <Typography sx={{fontSize: '0.85rem'}}>◀</Typography>
                        </IconButton>
                        <IconButton
                            size="small"
                            disabled={(calendarDrillPage + 1) * calendarDrillPageSize >= calendarDrillReports.length}
                            onClick={(): void => setCalendarDrillPage((p) => p + 1)}
                        >
                            <Typography sx={{fontSize: '0.85rem'}}>▶</Typography>
                        </IconButton>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    </>
);
